import { GridViewManager } from './../../../core/model/grid-view-manager';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { DataStateChangeEvent, GridComponent } from '@progress/kendo-angular-grid';
import { GroupDescriptor } from '@progress/kendo-data-query';
import { FormlyRuleNarrativePipe } from './formly-designer-rule-narrative.pipe';
import { AssessmentService } from 'src/app/core/service/assessment.service';
import { RuleService } from 'src/app/core/service/rule.service';
import { forkJoin } from 'rxjs';
import { sortBy } from 'lodash';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';

@Component({
  selector: 'app-formly-designer-summary',
  templateUrl: './formly-designer-summary.component.html',
  styleUrls: ['./formly-designer-summary.component.scss']
})
export class FormlyDesignerSummaryComponent implements OnInit
{
  @ViewChild('summaryGrid') public summaryGrid: GridComponent;

  @Input() public needs: UntypedFormArray;
  @Input() public flags: UntypedFormArray;
  @Input() public tasks: UntypedFormArray;
  @Input() public channels: UntypedFormArray;
  @Input() public serviceTypes: UntypedFormArray;
  @Input() public fields: FormlyFieldConfig[];

  public rulesView = new GridViewManager(99999);
  public gridData: any[] = [];
  public enableAdvanced = false;
  public expandAll = false;

  constructor(
    private translate: TranslateService,
    private ruleService: RuleService,
    private messageHandlerService: MessageHandlerService,
    private assessmentService: AssessmentService,
    private ruleNarrativePipe: FormlyRuleNarrativePipe) { }

  public ngOnInit(): void
  {
    this.loadRules();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent, view: GridViewManager): void
  {
    view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[], view: GridViewManager): void
  {
    view.setGroup(groups);
  }

  public toggleExpandable(e: boolean): void
  {
    this.expandAll = e;
    if (this.expandAll)
    {
      this.expandAllRows();
    }
    else
    {
      this.collapseAllRows();
    }
  }

  /**
   * Expand all.
   */
  private expandAllRows()
  {
    if (this.summaryGrid !== undefined)
    {
      for (let i = 0; i < this.rulesView.gridData.total; i++)
      {
        this.summaryGrid.expandRow(i);
      }
    }
  }

  /**
   * Collapse all.
   */
  private collapseAllRows()
  {
    if (this.summaryGrid !== undefined)
    {
      for (let i = 0; i < this.rulesView.gridData.total; i++)
      {
        this.summaryGrid.collapseRow(i);
      }
    }
  }

  private loadRules(): void
  {
    this.gridData = [];
    this.rulesView.startLoading();
    this.translate.get(['NEEDS', 'FLAGS', 'TASKS', 'CHANNELS', 'RISK-SCORES']).subscribe({
      next: (res) =>
      {
        // Prepare fields for narrative rendering
        const flatFields = this.assessmentService.getFlatAssessmentFields(this.fields);

        // Getting any dictionaries first
        const dictionaryObservables: any = this.ruleService.getFormlyFieldDictionaries(flatFields);

        // Load dictionaries identified
        const dictionaries = new Map();
        if (dictionaryObservables.length > 0)
        {
          forkJoin(dictionaryObservables).subscribe({
            next: (dictionaryData) =>
            {
              Object.keys(dictionaryData).forEach(e =>
              {
                // Check if we have dictionaries and not already loaded
                if (dictionaryData[e].length > 0 && !dictionaries.has(dictionaryData[e][0].codeSetId)) 
                {
                  dictionaries.set(dictionaryData[e][0].codeSetId, sortBy(dictionaryData[e], ['displayName']));
                }
              });
              this.continueLoadingSummary(dictionaries, flatFields, res);
            },
            error: (errorResponse: any) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
        else
        {
          this.continueLoadingSummary(dictionaries, flatFields, res);
        }
      }
    });
  }

  private continueLoadingSummary(dictionaries: any, flatFields: any, res: any): void 
  {
    // Load rules data
    this.loadRuleData(dictionaries, flatFields, this.flags, res.FLAGS, 'flags');
    this.loadRuleData(dictionaries, flatFields, this.needs, res.NEEDS, 'needs');
    this.loadRuleData(dictionaries, flatFields, this.tasks, res.TASKS, 'tasks');
    this.loadRuleData(dictionaries, flatFields, this.channels, res.CHANNELS, 'channels');
    this.loadRuleData(dictionaries, flatFields, this.serviceTypes, res['RISK-SCORES'], 'serviceTypes');

    // Load view data
    this.gridData.forEach(data =>
    {
      this.loadViewData(data, data.category);
    });

    this.rulesView.setData(this.gridData);
    this.rulesView.setSort([{ field: 'categoryName', dir: 'asc' }]);
    this.rulesView.stopLoading();

    setTimeout(() =>
    {
      if (this.expandAll)
      {
        this.expandAllRows();
      }
      else
      {
        this.collapseAllRows();
      }
    });
  }

  private loadRuleData(dictionaries: any, flatFields: any, data: any, categoryText: string, categoryKey: string): void
  {
    // Build narrative
    data.forEach(ruleData =>
    {
      ruleData.htmlData = this.ruleNarrativePipe.transform(dictionaries, ruleData.query, flatFields);
      this.gridData.push({ category: categoryKey, categoryName: categoryText, rule: ruleData });
    });
  }

  private loadViewData(data: any, categoryKey: string): void
  {
    const sectionGridData = [];
    switch (categoryKey)
    {
      case 'flags':
        sectionGridData.push({ description: data.rule.description, category: data.rule.category, priority: data.rule.priority });
        break;
      case 'needs':
        if (data.rule.serviceCategories)
        {
          data.rule.serviceCategories.forEach(categoryData =>
          {
            sectionGridData.push({ serviceCategory: categoryData });
          });
        }
        break;
      case 'tasks':
        sectionGridData.push({
          description: data.rule.description, dueDateDays: data.rule.dueDateDays, name: data.rule.name,
          priority: data.rule.priority, taskAssessment: data.rule.taskAssessment
        });
        break;
      case 'channels':
        sectionGridData.push({ channel: data.rule.channel, priority: data.rule.priority });
        break;
      case 'serviceTypes':
        sectionGridData.push({ serviceType: data.rule.serviceType, score: data.rule.score });
        break;
    }

    // Set grid data
    const sectionView = new GridViewManager(99999);
    sectionView.setData(sectionGridData);
    data.view = sectionView;
  }
}
